import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowDown, Loader2, Plus, Save, Trash2,
  ListChecks, CheckCircle2, Circle, RotateCcw, CalendarClock,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Admin-only to-do list. Two tabs ("Open" and "Done") — open items are
 * manually reorderable; done items act as a history (most-recently
 * ticked first). All endpoints require admin auth so visitors never
 * see this even by URL.
 */
export default function AdminTodos() {
  const [tab, setTab] = useState("open");
  const [openItems, setOpenItems] = useState(null);
  const [doneItems, setDoneItems] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newDue, setNewDue] = useState("");

  const refresh = async () => {
    try {
      const [oRes, dRes] = await Promise.all([
        api.get("/admin/todos?done=false"),
        api.get("/admin/todos?done=true"),
      ]);
      setOpenItems(oRes.data || []);
      setDoneItems(dRes.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/todos", {
        title,
        notes: newNotes.trim(),
        due_date: newDue,
      });
      setNewTitle("");
      setNewNotes("");
      setNewDue("");
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleDone = async (todo) => {
    setError("");
    try {
      await api.put(`/admin/todos/${todo.id}`, { done: !todo.done });
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (todo) => {
    if (!window.confirm(`Delete "${todo.title}"? This can't be undone.`)) return;
    setError("");
    try {
      await api.delete(`/admin/todos/${todo.id}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const move = async (idx, delta) => {
    if (!openItems) return;
    const target = idx + delta;
    if (target < 0 || target >= openItems.length) return;
    const next = [...openItems];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setOpenItems(next);
    try {
      await api.post("/admin/todos/reorder", { ids: next.map((t) => t.id) });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await refresh();
    }
  };

  const list = tab === "open" ? openItems : doneItems;

  const counts = useMemo(() => ({
    open: openItems?.length || 0,
    done: doneItems?.length || 0,
  }), [openItems, doneItems]);

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-todos"
    >
      <Header />
      <main className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <ListChecks size={16} strokeWidth={2.5} />
            <span className="label-kicker">To-do</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Your private reminders.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
            Stuff you want to remember to do on the site — only you see this.
            Tick items off to send them to the Done tab; un-tick to bring them
            back.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="todo-error"
          >
            {error}
          </p>
        ) : null}

        <form
          onSubmit={create}
          className="mb-8 rounded-md border border-black/10 bg-[#F8FAFC] p-5"
          data-testid="todo-create-form"
        >
          <p className="label-kicker text-[var(--brand-body)]">Add a reminder</p>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={200}
              placeholder="What needs doing? (e.g. update homepage hero image)"
              data-testid="todo-new-title"
              className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
            <textarea
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Notes (optional)"
              data-testid="todo-new-notes"
              className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="label-kicker text-[var(--brand-body)]">Due date (optional)</span>
                <input
                  type="date"
                  value={newDue}
                  onChange={(e) => setNewDue(e.target.value)}
                  data-testid="todo-new-due"
                  className="mt-1 block rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                />
              </label>
              <button
                type="submit"
                disabled={!newTitle.trim() || creating}
                data-testid="todo-create-submit"
                className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add to list
              </button>
            </div>
          </div>
        </form>

        {/* Tabs */}
        <div
          className="mb-4 flex gap-2 border-b border-black/10"
          role="tablist"
          aria-label="To-do filter"
          data-testid="todo-tabs"
        >
          <TabButton
            label={`Open (${counts.open})`}
            isActive={tab === "open"}
            onClick={() => setTab("open")}
            testid="todo-tab-open"
          />
          <TabButton
            label={`Done (${counts.done})`}
            isActive={tab === "done"}
            onClick={() => setTab("done")}
            testid="todo-tab-done"
          />
        </div>

        {list === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : list.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid={`todo-empty-${tab}`}
          >
            <p className="font-display text-lg font-bold">
              {tab === "open" ? "Nothing on the list. 🎉" : "Nothing ticked off yet."}
            </p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              {tab === "open"
                ? "Add a reminder above when something comes up."
                : "Tick an open item to log it here."}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid={`todo-list-${tab}`}>
            {list.map((t, idx) => (
              <TodoRow
                key={t.id}
                todo={t}
                onToggle={() => toggleDone(t)}
                onDelete={() => remove(t)}
                onSaved={refresh}
                onMoveUp={tab !== "open" || idx === 0 ? null : () => move(idx, -1)}
                onMoveDown={tab !== "open" || idx === list.length - 1 ? null : () => move(idx, 1)}
                setOuterError={setError}
              />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function TabButton({ label, isActive, onClick, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      data-testid={testid}
      className={`-mb-px border-b-2 px-4 py-2 font-display text-sm font-semibold transition-colors ${
        isActive
          ? "border-[#0A0F17] text-[var(--brand-ink)]"
          : "border-transparent text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
      }`}
    >
      {label}
    </button>
  );
}

function isOverdue(due, done) {
  if (!due || done) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${due}T00:00:00`);
  return d.getTime() < today.getTime();
}

function formatDue(due) {
  if (!due) return "";
  // Render in en-GB so "10 Jun 2026" not "Jun 10, 2026" — matches the
  // rest of the site's UK conventions.
  try {
    const d = new Date(`${due}T00:00:00`);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return due;
  }
}

function TodoRow({ todo, onToggle, onDelete, onSaved, onMoveUp, onMoveDown, setOuterError }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: todo.title,
    notes: todo.notes || "",
    due_date: todo.due_date || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ title: todo.title, notes: todo.notes || "", due_date: todo.due_date || "" });
  }, [todo.title, todo.notes, todo.due_date]);

  const dirty =
    form.title.trim() !== todo.title ||
    (form.notes || "").trim() !== (todo.notes || "") ||
    (form.due_date || "") !== (todo.due_date || "");

  const save = async () => {
    if (!form.title.trim() || !dirty) return;
    setSaving(true);
    setOuterError("");
    try {
      await api.put(`/admin/todos/${todo.id}`, {
        title: form.title.trim(),
        notes: form.notes.trim(),
        due_date: form.due_date,
      });
      setEditing(false);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const overdue = isOverdue(todo.due_date, todo.done);

  return (
    <li
      data-testid={`todo-row-${todo.id}`}
      className={`rounded-md border bg-white p-4 ${
        todo.done
          ? "border-black/10 bg-black/[0.02]"
          : overdue
          ? "border-[#E63946]/40"
          : "border-black/10"
      }`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          aria-label={todo.done ? "Mark as not done" : "Mark as done"}
          data-testid={`todo-${todo.id}-toggle`}
          className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
            todo.done
              ? "border-[#16A34A] bg-[#16A34A] text-white"
              : "border-black/30 text-transparent hover:border-[#0A0F17]"
          }`}
        >
          {todo.done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
        </button>

        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                maxLength={200}
                data-testid={`todo-${todo.id}-title`}
                className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                maxLength={2000}
                rows={2}
                placeholder="Notes (optional)"
                data-testid={`todo-${todo.id}-notes`}
                className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
              <label className="inline-flex items-center gap-2">
                <span className="label-kicker text-[var(--brand-body)]">Due</span>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                  data-testid={`todo-${todo.id}-due`}
                  className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
                />
                {form.due_date ? (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, due_date: "" }))}
                    data-testid={`todo-${todo.id}-clear-due`}
                    className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
                  >
                    clear
                  </button>
                ) : null}
              </label>
            </div>
          ) : (
            <>
              <p
                className={`font-display text-base font-bold leading-snug ${
                  todo.done ? "text-[var(--brand-body)] line-through" : "text-[var(--brand-ink)]"
                }`}
                data-testid={`todo-${todo.id}-title-display`}
              >
                {todo.title}
              </p>
              {todo.notes ? (
                <p
                  className={`mt-1 whitespace-pre-line text-sm ${
                    todo.done ? "text-[var(--brand-body)]/70" : "text-[var(--brand-body)]"
                  }`}
                  data-testid={`todo-${todo.id}-notes-display`}
                >
                  {todo.notes}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center gap-3 label-kicker text-[var(--brand-body)]">
                {todo.due_date ? (
                  <span
                    className={`inline-flex items-center gap-1 ${overdue ? "text-[#E63946]" : ""}`}
                    data-testid={`todo-${todo.id}-due-display`}
                  >
                    <CalendarClock size={11} /> Due {formatDue(todo.due_date)}
                    {overdue ? " · overdue" : ""}
                  </span>
                ) : null}
                {todo.done && todo.completed_at ? (
                  <span data-testid={`todo-${todo.id}-completed-display`}>
                    Done {new Date(todo.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                ) : null}
              </div>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {!todo.done && !editing ? (
            <>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={!onMoveUp}
                aria-label="Move up"
                data-testid={`todo-${todo.id}-up`}
                className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-30"
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={!onMoveDown}
                aria-label="Move down"
                data-testid={`todo-${todo.id}-down`}
                className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-30"
              >
                <ArrowDown size={12} />
              </button>
            </>
          ) : null}
          {editing ? (
            <>
              <button
                type="button"
                onClick={save}
                disabled={!dirty || !form.title.trim() || saving}
                data-testid={`todo-${todo.id}-save`}
                className="inline-flex items-center gap-1 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-semibold text-white hover:bg-brand-accent disabled:opacity-40"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm({ title: todo.title, notes: todo.notes || "", due_date: todo.due_date || "" });
                  setEditing(false);
                }}
                data-testid={`todo-${todo.id}-cancel`}
                className="rounded-md border border-black/10 px-2 py-1.5 label-kicker text-[var(--brand-body)] hover:border-[#0A0F17]"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid={`todo-${todo.id}-edit`}
              className="rounded-md border border-black/10 px-2 py-1.5 label-kicker text-[var(--brand-body)] hover:border-[#0A0F17]"
            >
              Edit
            </button>
          )}
          {todo.done ? (
            <button
              type="button"
              onClick={onToggle}
              data-testid={`todo-${todo.id}-reopen`}
              title="Move back to Open"
              className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17]"
            >
              <RotateCcw size={12} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            data-testid={`todo-${todo.id}-delete`}
            className="rounded-md border border-[#E63946]/40 p-1.5 text-[#E63946] hover:bg-[#E63946] hover:text-white"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </li>
  );
}
